import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The Pro header's cover: the photo, with an optional video that fades in over it.
 *
 * The photo is never removed — it paints immediately and stays underneath as the
 * poster, so a slow connection, a decoding failure or a browser that blocks
 * autoplay all degrade to exactly the page we had before. The video only becomes
 * visible once it is genuinely playing (`playing`, not `canplay`, which fires
 * before the first frame is on screen and would flash black).
 *
 * NO CHROME, EVER
 * This is decoration, not a player, so nothing of a player may show: not the
 * native controls, not YouTube's centre play/pause + skip buttons, not its title
 * bar. Three things together guarantee that:
 *   1. The upload is a controlless, muted, inline <video> (the WebKit controls are
 *      also suppressed in CSS, and `pointer-events: none` means a tap can never
 *      pause it into showing them).
 *   2. A YouTube embed is driven through the IFrame API (`enablejsapi`) and is
 *      revealed only once YouTube itself reports PLAYING — and, on a touch device,
 *      after the burst of controls YouTube paints at the start of playback has
 *      faded. Anything else (unstarted, paused, buffering, ended) shows the photo.
 *      Vimeo's `background=1` mode has no chrome at all and gets a timed fade.
 *   3. Autoplay that a browser refuses (iOS Safari in Low Power Mode, an embed
 *      before any gesture) is retried on the page's first touch, click, key or
 *      scroll — a muted play is allowed inside any user gesture, and the tap that
 *      opened the page's first thing is enough.
 *
 * FILLING THE BOX, AND WHY THAT HIDES THE BRANDING
 * The slot is much taller than any 16:9 video, so the player is scaled up and
 * cropped to cover it — the same thing `object-fit: cover` does for an image.
 * That crop is what carries YouTube's and Vimeo's corner marks off-frame.
 */

/** How much bigger than the box an embed is drawn, so its corners fall outside. */
const EMBED_OVERSCAN = 1.35;
/** How long YouTube's start-of-playback controls stay up on a phone before they auto-hide. */
const YT_CONTROLS_FADE_MS = 2600;
const YT_ORIGINS = ['https://www.youtube-nocookie.com', 'https://www.youtube.com'];
const YT_PLAYING = 1;

const isTouchDevice = () => typeof window !== 'undefined' && !!(window.matchMedia?.('(hover: none) and (pointer: coarse)')?.matches);

/** The events that count as the user having touched the page (a muted play is allowed inside any of them). */
const GESTURES = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'];

/** Add the IFrame-API parameters to a YouTube embed src, once. */
export function youtubeApiSrc(embedUrl) {
  if (!embedUrl) return embedUrl;
  try {
    const u = new URL(embedUrl, typeof window !== 'undefined' ? window.location.href : 'https://heyportal.app');
    if (!YT_ORIGINS.some((o) => u.origin === o)) return embedUrl;
    u.searchParams.set('enablejsapi', '1');
    if (typeof window !== 'undefined' && /^https?:\/\//.test(window.location.origin)) u.searchParams.set('origin', window.location.origin);
    return u.toString();
  } catch { return embedUrl; }
}

export default function ProCoverVideo({ photoUrl, video, alt = '', className = '', objectPosition = 'center' }) {
  const ready = !!video?.ready;
  const isUpload = video?.provider === 'upload';
  // A plain video file we serve ourselves (`fileUrl`) — the /pro landing page's sample
  // cover. Same element, same fade, no HLS; a real pro's upload is always `upload`.
  const isFile = video?.provider === 'file';
  const isNative = isUpload || isFile;
  const isYouTube = ready && !isNative && video?.provider === 'youtube' && !!video?.embedUrl;
  const isOtherEmbed = ready && !isNative && !isYouTube && !!video?.embedUrl;
  const [showing, setShowing] = useState(false);
  const videoRef = useRef(null);
  const frameRef = useRef(null);
  const revealTimer = useRef(null);
  const embedSrc = isYouTube ? youtubeApiSrc(video.embedUrl) : video?.embedUrl;

  // hls.js only for uploads. Safari plays HLS natively, so it's loaded lazily and
  // only where it's actually needed — it has no business in the bundle otherwise.
  useEffect(() => {
    if (!ready || !isUpload || !video?.hlsUrl) return undefined;
    const el = videoRef.current;
    if (!el) return undefined;

    let hls;
    let cancelled = false;

    if (el.canPlayType('application/vnd.apple.mpegurl')) {
      el.src = video.hlsUrl;
    } else {
      (async () => {
        const { default: Hls } = await import('hls.js');
        if (cancelled || !videoRef.current) return;
        if (!Hls.isSupported()) return;
        hls = new Hls({ capLevelToPlayerSize: true });
        hls.loadSource(video.hlsUrl);
        hls.attachMedia(videoRef.current);
      })();
    }

    return () => { cancelled = true; if (hls) hls.destroy(); };
  }, [ready, isUpload, video?.hlsUrl]);

  /** Tell the player to play, muted. Safe to call any number of times. */
  const kick = useCallback(() => {
    const el = videoRef.current;
    if (el) {
      el.muted = true;
      if (el.paused) { const p = el.play(); if (p?.catch) p.catch(() => {}); }
    }
    const frame = frameRef.current;
    if (frame?.contentWindow) {
      if (isYouTube) {
        const post = (func) => frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*');
        post('mute'); post('playVideo');
      } else if (isOtherEmbed) {
        // Vimeo's player.js message shape; other providers ignore it.
        frame.contentWindow.postMessage(JSON.stringify({ method: 'setVolume', value: 0 }), '*');
        frame.contentWindow.postMessage(JSON.stringify({ method: 'play' }), '*');
      }
    }
  }, [isYouTube, isOtherEmbed]);

  // Autoplay the browser refused starts on the first gesture anywhere on the page.
  // Listeners are passive and capturing so nothing else on the page can swallow them.
  useEffect(() => {
    if (!ready) return undefined;
    const onGesture = () => kick();
    const onVisible = () => { if (document.visibilityState === 'visible') kick(); };
    GESTURES.forEach((ev) => document.addEventListener(ev, onGesture, { passive: true, capture: true }));
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      GESTURES.forEach((ev) => document.removeEventListener(ev, onGesture, { capture: true }));
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [ready, kick]);

  // YouTube: subscribe to the player's state and reveal it only while it is PLAYING.
  useEffect(() => {
    if (!isYouTube) return undefined;
    const onMessage = (e) => {
      if (!YT_ORIGINS.includes(e.origin)) return;
      let data = e.data;
      if (typeof data === 'string') { try { data = JSON.parse(data); } catch { return; } }
      if (!data || typeof data !== 'object') return;
      let state = null;
      if (data.event === 'onStateChange') state = data.info;
      else if (data.event === 'infoDelivery' && data.info && typeof data.info.playerState === 'number') state = data.info.playerState;
      else if (data.event === 'onReady') { kick(); return; }
      if (state == null) return;
      clearTimeout(revealTimer.current);
      if (state === YT_PLAYING) {
        // On a phone YouTube paints its play/pause + skip buttons as playback starts;
        // hold the photo over it until they have faded.
        if (isTouchDevice()) revealTimer.current = setTimeout(() => setShowing(true), YT_CONTROLS_FADE_MS);
        else setShowing(true);
      } else {
        setShowing(false);
      }
    };
    window.addEventListener('message', onMessage);
    return () => { window.removeEventListener('message', onMessage); clearTimeout(revealTimer.current); };
  }, [isYouTube, kick]);

  // Vimeo's background mode has no chrome and can't tell us when it started, so reveal
  // it once it has had a moment to load. Getting this wrong only costs a slightly
  // late fade, never a black box.
  useEffect(() => {
    if (!isOtherEmbed) return undefined;
    const t = setTimeout(() => setShowing(true), 900);
    return () => clearTimeout(t);
  }, [isOtherEmbed]);

  const onFrameLoad = () => {
    const frame = frameRef.current;
    if (isYouTube && frame?.contentWindow) {
      // Ask for events; the API answers with onReady / onStateChange.
      frame.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 'pro-cover', channel: 'widget' }), '*');
    }
    kick();
  };

  return (
    <div className={`relative h-full w-full overflow-hidden bg-black ${className}`}>
      {photoUrl && (
        <img
          src={photoUrl}
          alt={alt}
          className="absolute inset-0 h-full w-full object-cover"
          // The tile's framing (drag-to-reframe on the Pro cover) applies to the poster too.
          style={{ objectPosition }}
          draggable={false}
          // Stays put underneath rather than being swapped out — it's the fallback
          // for every way the video can fail to arrive.
        />
      )}

      {ready && isNative && (
        <video
          ref={videoRef}
          src={isFile ? video.fileUrl : undefined}
          className="pr-cover-video pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-700"
          style={{ opacity: showing ? 1 : 0 }}
          // Muted is what makes autoplay legal in every browser; playsInline stops
          // iOS taking it fullscreen; the rest keep every kind of chrome away.
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          controls={false}
          disablePictureInPicture
          disableRemotePlayback
          tabIndex={-1}
          aria-hidden="true"
          onPlaying={() => setShowing(true)}
          onPause={() => { setShowing(false); kick(); }}
          onError={() => setShowing(false)}
        />
      )}

      {(isYouTube || isOtherEmbed) && (
        // Scaled past the edges so the provider's corner marks land outside the
        // box. pointer-events:none because a pause is what makes their chrome
        // appear, and there's nothing here to interact with.
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 transition-opacity duration-700"
          style={{
            opacity: showing ? 1 : 0,
            width: `${EMBED_OVERSCAN * 100}%`,
            // 16:9 kept exactly, then over-scanned, so the frame is never squashed.
            height: `${EMBED_OVERSCAN * 100}%`,
            minWidth: `${EMBED_OVERSCAN * 177.78}vh`,
            minHeight: `${EMBED_OVERSCAN * 56.25}vw`,
            transform: 'translate(-50%, -50%)',
          }}
          aria-hidden="true"
        >
          <iframe
            ref={frameRef}
            src={embedSrc}
            title="Cover video"
            className="h-full w-full border-0"
            allow="autoplay; encrypted-media; picture-in-picture"
            referrerPolicy="strict-origin"
            tabIndex={-1}
            loading="lazy"
            onLoad={onFrameLoad}
          />
        </div>
      )}
      <style>{'.pr-cover-video::-webkit-media-controls, .pr-cover-video::-webkit-media-controls-panel, .pr-cover-video::-webkit-media-controls-start-playback-button { display: none !important; -webkit-appearance: none; }'}</style>
    </div>
  );
}
